import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  FaChartLine, FaUsers, FaCheckCircle, FaClock, FaExclamationTriangle, 
  FaDownload, FaCalendarWeek, FaCalendarAlt, FaSync, FaFilter, 
  FaSearch, FaSortAmountDown, FaSortAmountUp, FaUserCheck 
} from 'react-icons/fa';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area 
} from 'recharts';

const UsersStats = () => {
    const [stats, setStats] = useState([]);
    const [filteredStats, setFilteredStats] = useState([]);
    const [period, setPeriod] = useState('monthly');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'total_tasks', direction: 'desc' });
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [departments, setDepartments] = useState([]);
    const [chartType, setChartType] = useState('bar');
    const [summaryCards, setSummaryCards] = useState({
        totalUsers: 0,
        totalTasks: 0,
        totalCompleted: 0,
        totalOverdue: 0,
        overallCompletionRate: 0,
        activeUsers: 0
    });

    // Fetch stats with abort controller for cleanup
    useEffect(() => {
        const abortController = new AbortController();
        fetchStats(abortController.signal);
        return () => abortController.abort();
    }, [period]);

    const fetchStats = async (signal) => {
        setLoading(true);
        try {
            const response = await axios.get(`/api/stats/users?period=${period}`, {
                signal,
                timeout: 10000
            });
            
            if (response.data && response.data.stats) {
                const statsData = response.data.stats;
                setStats(statsData);
                setFilteredStats(statsData);
                
                // Extract unique departments
                const uniqueDepts = [...new Set(statsData.map(user => user.department).filter(Boolean))];
                setDepartments(uniqueDepts);
                
                // Calculate summary statistics
                calculateSummaryStats(statsData);
            } else {
                setStats([]);
                setFilteredStats([]);
                toast.error('Invalid data format received');
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
                console.error('Error fetching stats:', error);
                let errorMessage = 'Failed to load statistics';
                if (error.response?.status === 404) {
                    errorMessage = 'Statistics endpoint not found';
                } else if (error.response?.status === 500) {
                    errorMessage = 'Server error while loading statistics';
                } else if (error.code === 'ECONNABORTED') {
                    errorMessage = 'Request timeout. Please try again';
                }
                toast.error(errorMessage);
                setStats([]);
                setFilteredStats([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const calculateSummaryStats = (data) => {
        const totalUsers = data.length;
        const totalTasks = data.reduce((sum, u) => sum + (Number(u.total_tasks) || 0), 0);
        const totalCompleted = data.reduce((sum, u) => sum + (Number(u.completed_tasks) || 0), 0);
        const totalOverdue = data.reduce((sum, u) => sum + (Number(u.overdue_tasks) || 0), 0);
        const overallCompletionRate = totalTasks > 0 ? ((totalCompleted / totalTasks) * 100).toFixed(1) : 0;
        const activeUsers = data.filter(u => (u.pending_tasks || 0) > 0 || (u.overdue_tasks || 0) > 0).length;

        setSummaryCards({
            totalUsers,
            totalTasks,
            totalCompleted,
            totalOverdue,
            overallCompletionRate,
            activeUsers
        });
    };

    // Memoized completion rate calculation
    const getCompletionRate = useCallback((user) => {
        if (!user) return 0;
        const total = Number(user.total_tasks) || 0;
        const completed = Number(user.completed_tasks) || 0;
        if (total === 0) return 0;
        return ((completed / total) * 100).toFixed(1);
    }, []);

    const formatAvgDays = useCallback((days) => {
        if (!days && days !== 0) return 'N/A';
        const numDays = Number(days);
        if (isNaN(numDays)) return 'N/A';
        if (numDays === 0) return '< 1 day';
        return `${numDays.toFixed(1)} day${numDays !== 1 ? 's' : ''}`;
    }, []);

    // Filter and sort users
    useEffect(() => {
        let result = [...stats];
        
        // Apply search filter
        if (searchTerm) {
            result = result.filter(user => 
                user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.mobile?.includes(searchTerm) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Apply department filter
        if (departmentFilter !== 'all') {
            result = result.filter(user => user.department === departmentFilter);
        }
        
        // Apply sorting
        result.sort((a, b) => {
            let aValue = a[sortConfig.key] || 0;
            let bValue = b[sortConfig.key] || 0;
            
            if (sortConfig.key === 'completion_rate') {
                aValue = parseFloat(getCompletionRate(a));
                bValue = parseFloat(getCompletionRate(b));
            } else if (sortConfig.key === 'name') {
                aValue = a.name || '';
                bValue = b.name || '';
                return sortConfig.direction === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                aValue = Number(aValue);
                bValue = Number(bValue);
            }
            
            if (sortConfig.direction === 'asc') {
                return aValue - bValue;
            } else {
                return bValue - aValue;
            }
        });
        
        setFilteredStats(result);
    }, [stats, searchTerm, departmentFilter, sortConfig, getCompletionRate]);

    // Chart data with memoization
    const chartData = useMemo(() => {
        return filteredStats.map(user => ({
            name: user.name?.length > 15 ? user.name.substring(0, 15) + '...' : user.name,
            fullName: user.name,
            Completed: Number(user.completed_tasks) || 0,
            Pending: Number(user.pending_tasks) || 0,
            Overdue: Number(user.overdue_tasks) || 0,
            completionRate: parseFloat(getCompletionRate(user))
        }));
    }, [filteredStats, getCompletionRate]);

    const pieData = useMemo(() => {
        return [
            { name: 'Completed', value: summaryCards.totalCompleted, color: '#10B981' },
            { name: 'Pending', value: summaryCards.totalTasks - summaryCards.totalCompleted - summaryCards.totalOverdue, color: '#F59E0B' },
            { name: 'Overdue', value: summaryCards.totalOverdue, color: '#EF4444' }
        ].filter(item => item.value > 0);
    }, [summaryCards]);

    const COLORS = ['#10B981', '#F59E0B', '#EF4444'];

    // Export functionality with improved formatting
    const exportStats = useCallback(() => {
        if (filteredStats.length === 0) {
            toast.error('No data to export');
            return;
        }

        try {
            const headers = ['Name', 'Email', 'Mobile', 'Department', 'Total Tasks', 'Completed', 'Pending', 'Overdue', 'Completion Rate', 'Avg Completion Days'];
            const rows = filteredStats.map(user => [
                `"${user.name || ''}"`,
                `"${user.email || ''}"`,
                `"${user.mobile || ''}"`,
                `"${user.department || '-'}"`,
                user.total_tasks || 0,
                user.completed_tasks || 0,
                user.pending_tasks || 0,
                user.overdue_tasks || 0,
                `${getCompletionRate(user)}%`,
                formatAvgDays(user.avg_completion_days)
            ]);

            const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
            const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `user_stats_${period}_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`Exported ${filteredStats.length} records successfully`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export data');
        }
    }, [filteredStats, period, getCompletionRate, formatAvgDays]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? <FaSortAmountUp className="inline ml-1" /> : <FaSortAmountDown className="inline ml-1" />;
    };

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-500">Loading statistics...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            {/* Header Section */}
            <div className="mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
                            <FaChartLine className="text-blue-500" />
                            User Statistics
                        </h1>
                        <p className="text-gray-500 mt-1">Performance metrics and detailed analytics</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <div className="flex gap-2 bg-white rounded-lg shadow-sm p-1">
                            {['weekly', 'monthly', 'yearly'].map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setPeriod(p)}
                                    className={`px-4 py-2 rounded-md transition-all flex items-center gap-2 capitalize ${
                                        period === p 
                                            ? 'bg-blue-500 text-white shadow-md' 
                                            : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    {p === 'weekly' ? <FaCalendarWeek /> : <FaCalendarAlt />}
                                    {p}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => fetchStats()}
                            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2"
                            disabled={loading}
                        >
                            <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
                        </button>
                        <button
                            onClick={exportStats}
                            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all flex items-center gap-2"
                        >
                            <FaDownload /> Export CSV
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm">Total Users</p>
                                <p className="text-3xl font-bold mt-2">{summaryCards.totalUsers}</p>
                                <p className="text-xs text-green-600 mt-1">
                                    <FaUserCheck className="inline mr-1" />
                                    {summaryCards.activeUsers} active
                                </p>
                            </div>
                            <FaUsers className="text-4xl text-blue-500 opacity-50" />
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm">Total Tasks</p>
                                <p className="text-3xl font-bold mt-2">{summaryCards.totalTasks}</p>
                                <p className="text-xs text-gray-500 mt-1">Across all users</p>
                            </div>
                            <FaChartLine className="text-4xl text-purple-500 opacity-50" />
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm">Completed Tasks</p>
                                <p className="text-3xl font-bold mt-2 text-green-600">{summaryCards.totalCompleted}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                    {summaryCards.overallCompletionRate}% completion rate
                                </p>
                            </div>
                            <FaCheckCircle className="text-4xl text-green-500 opacity-50" />
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm">Overdue Tasks</p>
                                <p className="text-3xl font-bold mt-2 text-red-600">{summaryCards.totalOverdue}</p>
                                <p className="text-xs text-orange-600 mt-1">
                                    <FaClock className="inline mr-1" />
                                    Need attention
                                </p>
                            </div>
                            <FaExclamationTriangle className="text-4xl text-red-500 opacity-50" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">Task Distribution</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setChartType('pie')}
                                className={`px-3 py-1 rounded text-sm ${chartType === 'pie' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                            >
                                Pie
                            </button>
                            <button
                                onClick={() => setChartType('donut')}
                                className={`px-3 py-1 rounded text-sm ${chartType === 'donut' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                            >
                                Donut
                            </button>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}% (${pieData.find(d => d.name === name)?.value})`}
                                outerRadius={chartType === 'donut' ? 80 : 100}
                                innerRadius={chartType === 'donut' ? 50 : 0}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} stroke="#fff" strokeWidth={2} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value} tasks`, 'Count']} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">User Performance Overview</h2>
                    {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={chartData.slice(0, 10)}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis 
                                    dataKey="name" 
                                    angle={-45} 
                                    textAnchor="end" 
                                    height={80} 
                                    interval={0}
                                    tick={{ fontSize: 12 }}
                                />
                                <YAxis />
                                <Tooltip 
                                    formatter={(value, name) => [`${value} tasks`, name]}
                                    labelFormatter={(label) => `User: ${chartData.find(d => d.name === label)?.fullName || label}`}
                                />
                                <Legend />
                                <Bar dataKey="Completed" fill="#10B981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Pending" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Overdue" fill="#EF4444" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-64 text-gray-500">
                            No data available for this period
                        </div>
                    )}
                </div>
            </div>

            {/* Filters and User Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <h2 className="text-lg font-semibold">Detailed User Statistics</h2>
                        <div className="flex flex-wrap gap-3">
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name, email or mobile..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <select
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                {['name', 'Department', 'total_tasks', 'completed_tasks', 'pending_tasks', 'overdue_tasks', 'completion_rate', 'avg_completion_days'].map((key) => (
                                    <th 
                                        key={key}
                                        onClick={() => handleSort(key)}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            {key.replace('_', ' ').replace('avg', 'Avg').replace('completion', 'Completion')}
                                            {getSortIcon(key)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
    {filteredStats.map((user) => (
        <tr key={user.id || user.mobile || index} className="hover:bg-gray-50 transition-colors">
            <td className="px-6 py-4">
                <div className="font-medium text-gray-900">{user.name}</div>
                <div className="text-xs text-gray-500">{user.mobile}</div>
                {user.email && <div className="text-xs text-gray-400">{user.email}</div>}
            </td>
            <td className="px-6 py-4">
                <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                    {user.department || 'N/A'}
                </span>
            </td>
            <td className="px-6 py-4 font-semibold text-gray-900">{user.total_tasks || 0}</td>
            <td className="px-6 py-4">
                <span className="text-green-600 font-medium">{user.completed_tasks || 0}</span>
            </td>
            <td className="px-6 py-4">
                <span className="text-yellow-600 font-medium">{user.pending_tasks || 0}</span>
            </td>
            <td className="px-6 py-4">
                <span className="text-red-600 font-medium">{user.overdue_tasks || 0}</span>
            </td>
            <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-green-500 rounded-full transition-all"
                            style={{ width: `${getCompletionRate(user)}%` }}
                        ></div>
                    </div>
                    <span className="text-sm font-medium min-w-[45px]">{getCompletionRate(user)}%</span>
                </div>
            </td>
            <td className="px-6 py-4 text-gray-600">
                {formatAvgDays(user.avg_completion_days)}
            </td>
        </tr>
    ))}
</tbody>
                    </table>
                </div>
                
                {filteredStats.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-gray-500">No statistics available for the selected filters</p>
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setDepartmentFilter('all');
                            }}
                            className="mt-2 text-blue-500 hover:text-blue-600"
                        >
                            Clear filters
                        </button>
                    </div>
                )}
                
                {filteredStats.length > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                        <p className="text-sm text-gray-600">
                            Showing {filteredStats.length} of {stats.length} users
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UsersStats;